import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import { superAdminApi, type SuperAdminOverviewRow, type SuperAdminUser } from '../services/superadmin';
import { TableHead, TableWrapper } from '../components/TableWrapper';

function nameOf(row: { firstName: string | null; lastName: string | null; email: string }): string {
  const name = `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim();
  return name || row.email;
}

export function SuperAdminPage() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [superAdmins, setSuperAdmins] = useState<SuperAdminUser[]>([]);
  const [rows, setRows] = useState<SuperAdminOverviewRow[]>([]);
  const [busyUserId, setBusyUserId] = useState<number | null>(null);
  const [busyPurchaseId, setBusyPurchaseId] = useState<number | null>(null);
  const [promoteUserId, setPromoteUserId] = useState<number>(0);

  const load = useCallback(async () => {
    try {
      const [sa, ov] = await Promise.all([superAdminApi.listSuperAdmins(), superAdminApi.overview()]);
      setSuperAdmins(sa.superAdmins);
      setRows(ov.rows);
      if (!promoteUserId && ov.rows.length > 0) {
        setPromoteUserId(ov.rows[0].userId);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte ladda superadmin-data');
    } finally {
      setLoading(false);
    }
  }, [toast, promoteUserId]);

  useEffect(() => {
    load();
  }, [load]);

  const users = useMemo(() => {
    const seen = new Set<number>();
    return rows.filter((r) => {
      if (seen.has(r.userId)) return false;
      seen.add(r.userId);
      return true;
    });
  }, [rows]);

  const handlePromote = async () => {
    if (promoteUserId < 1) return;
    setBusyUserId(promoteUserId);
    try {
      await superAdminApi.promote(promoteUserId);
      toast.success('Användaren är nu SUPERADMIN');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte promota användare');
    } finally {
      setBusyUserId(null);
    }
  };

  const handleDemote = async (userId: number) => {
    setBusyUserId(userId);
    try {
      await superAdminApi.demote(userId);
      toast.success('SUPERADMIN-rollen togs bort');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte ta bort rollen');
    } finally {
      setBusyUserId(null);
    }
  };

  const handleApprove = async (purchaseId: number) => {
    setBusyPurchaseId(purchaseId);
    try {
      await superAdminApi.approvePendingPurchase(purchaseId);
      toast.success('Uppgraderingen är godkänd');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte godkänna uppgradering');
    } finally {
      setBusyPurchaseId(null);
    }
  };

  if (loading) return <p className="text-sm text-gray-500">Laddar...</p>;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Superadmin</h2>

      <section className="rounded border border-gray-200 p-3 space-y-2">
        <h3 className="font-medium">SUPERADMIN-lista</h3>
        <div className="space-y-1">
          {superAdmins.map((u) => (
            <div key={u.id} className="flex items-center justify-between text-sm border-b border-gray-100 py-1">
              <span>{nameOf(u)} ({u.email})</span>
              <button
                type="button"
                onClick={() => handleDemote(u.id)}
                disabled={busyUserId === u.id}
                className="px-2 py-1 rounded bg-danger text-white hover:bg-danger-hover disabled:opacity-50"
              >
                Unpromote
              </button>
            </div>
          ))}
        </div>

        <div className="pt-2 flex items-center gap-2">
          <select
            className="px-2 py-1 border border-gray-300 rounded text-sm"
            value={promoteUserId}
            onChange={(e) => setPromoteUserId(Number(e.target.value))}
          >
            {users.map((u) => (
              <option key={u.userId} value={u.userId}>
                {nameOf({ firstName: u.firstName, lastName: u.lastName, email: u.email })} ({u.email})
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={handlePromote}
            disabled={promoteUserId < 1 || busyUserId === promoteUserId}
            className="px-2 py-1 rounded bg-success text-white hover:bg-success-hover disabled:opacity-50"
          >
            Promote till SUPERADMIN
          </button>
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="font-medium">Alla användare och planer</h3>
        <TableWrapper>
          <table className="w-full text-sm border-collapse">
            <TableHead>
              <tr>
                <th className="text-left px-2 py-1.5 font-medium">Användare</th>
                <th className="text-left px-2 py-1.5 font-medium">Företag</th>
                <th className="text-left px-2 py-1.5 font-medium">Plan</th>
                <th className="text-left px-2 py-1.5 font-medium">Pending</th>
                <th className="text-left px-2 py-1.5 font-medium">Åtgärd</th>
              </tr>
            </TableHead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.userId}-${r.businessId ?? 0}-${r.pendingPurchaseId ?? 0}`} className="border-t border-gray-100">
                  <td className="px-2 py-1.5">{nameOf({ firstName: r.firstName, lastName: r.lastName, email: r.email })}</td>
                  <td className="px-2 py-1.5">{r.businessName ?? '-'}</td>
                  <td className="px-2 py-1.5">{r.planCode ?? '-'}</td>
                  <td className="px-2 py-1.5">
                    {r.pendingPurchaseId ? `${r.pendingTargetPlanCode} (${r.pendingAmountToPaySek ?? 0} kr)` : '-'}
                  </td>
                  <td className="px-2 py-1.5">
                    {r.pendingPurchaseId ? (
                      <button
                        type="button"
                        onClick={() => handleApprove(r.pendingPurchaseId!)}
                        disabled={busyPurchaseId === r.pendingPurchaseId}
                        className="px-2 py-1 rounded bg-success text-white hover:bg-success-hover disabled:opacity-50"
                      >
                        Godkänn uppgradering
                      </button>
                    ) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrapper>
      </section>
    </div>
  );
}

